import { useMemo } from "react";
import { useQuery } from "@tanstack/react-query";
import { customFetch } from "@workspace/api-client-react";
import { PieChart, Pie, Cell, Tooltip, ResponsiveContainer } from "recharts";
import { Card, CardContent, CardHeader, CardTitle } from "@/components/ui/card";

type Segmento = { key: string; label: string; n: number };

// Pizza só lê bem com até 6 fatias: os 5 nichos grandes de comida + "Outros".
// A cor é FIXA por nicho (nunca pela posição no ranking): se a ordem mudar, nenhuma
// fatia troca de cor. Paleta categórica validada (CVD e contraste) no claro e no
// escuro; no claro três cores ficam abaixo de 3:1 contra o fundo, por isso a lista
// ao lado traz número e % de cada nicho.
const FATIAS = ["hamburgueria", "acai", "doceria", "marmitaria", "pizzaria"] as const;
const COR: Record<string, string> = {
  hamburgueria: "var(--seg-1)",
  acai: "var(--seg-2)",
  doceria: "var(--seg-3)",
  marmitaria: "var(--seg-4)",
  pizzaria: "var(--seg-5)",
  outros: "var(--seg-outros)",
};
const CSS = `
.viz-seg { --seg-1:#2a78d6; --seg-2:#eb6834; --seg-3:#1baf7a; --seg-4:#eda100; --seg-5:#e87ba4; --seg-outros:#b4b2a9; }
.dark .viz-seg { --seg-1:#3987e5; --seg-2:#d95926; --seg-3:#199e70; --seg-4:#c98500; --seg-5:#d55181; --seg-outros:#6e6d66; }
`;

const pct = (n: number, total: number) => {
  if (total <= 0 || n <= 0) return "0%";
  const v = (n / total) * 100;
  return v < 1 ? "<1%" : `${Math.round(v)}%`;
};

// Rótulo de fora na cor de TEXTO (nunca a da fatia) e só nas fatias >= 8%.
function rotuloFora(p: { cx: number; cy: number; midAngle: number; outerRadius: number; percent: number }) {
  if (!p.percent || p.percent < 0.08) return null;
  const r = p.outerRadius + 14;
  const a = (-p.midAngle * Math.PI) / 180;
  const x = p.cx + r * Math.cos(a);
  const y = p.cy + r * Math.sin(a);
  return (
    <text x={x} y={y} textAnchor={x >= p.cx ? "start" : "end"} dominantBaseline="central" fontSize={12} fill="hsl(var(--muted-foreground))">
      {`${Math.round(p.percent * 100)}%`}
    </text>
  );
}

export function SegmentPieCard() {
  const { data, isLoading, isError } = useQuery<{ total: number; segments: Segmento[] }>({
    queryKey: ["admin-results-segments"],
    queryFn: () => customFetch("/api/admin/results/segments"),
  });

  const { fatias, total } = useMemo(() => {
    const segs = data?.segments ?? [];
    const total = data?.total ?? 0;
    const principais = FATIAS.map((k) => segs.find((s) => s.key === k)).filter((s): s is Segmento => !!s && s.n > 0);
    const resto = segs.filter((s) => !FATIAS.includes(s.key as (typeof FATIAS)[number])).reduce((t, s) => t + s.n, 0);
    const fatias = [
      ...principais.map((s) => ({ key: s.key, label: s.label, n: s.n })),
      ...(resto > 0 ? [{ key: "outros", label: "Outros", n: resto }] : []),
    ];
    return { fatias, total };
  }, [data]);

  return (
    <Card className="viz-seg">
      <style>{CSS}</style>
      <CardHeader className="pb-2">
        <CardTitle className="text-base">Nicho dos assinantes</CardTitle>
      </CardHeader>
      <CardContent>
        {isLoading ? (
          <p className="text-sm text-muted-foreground">Carregando…</p>
        ) : isError ? (
          <p className="text-sm text-destructive">Não deu pra carregar os nichos.</p>
        ) : total === 0 ? (
          <p className="text-sm text-muted-foreground">Nenhum assinante classificado ainda.</p>
        ) : (
          <div className="grid gap-4 md:grid-cols-2 items-center">
            <div className="h-64" role="img" aria-label={`Pizza dos nichos de ${total} assinantes`}>
              <ResponsiveContainer width="100%" height="100%">
                <PieChart>
                  <Pie
                    data={fatias}
                    dataKey="n"
                    nameKey="label"
                    outerRadius="72%"
                    stroke="var(--color-card)"
                    strokeWidth={2}
                    isAnimationActive={false}
                    labelLine={false}
                    label={rotuloFora as never}
                  >
                    {fatias.map((f) => (
                      <Cell key={f.key} fill={COR[f.key] ?? COR.outros} />
                    ))}
                  </Pie>
                  <Tooltip
                    formatter={(v: number, nome: string) => [`${v} ${v === 1 ? "loja" : "lojas"} (${pct(v, total)})`, nome]}
                  />
                </PieChart>
              </ResponsiveContainer>
            </div>
            {/* A lista é a legenda E a tabela: nome, quantidade e % de TODOS os nichos. */}
            <ul className="space-y-1.5 text-sm">
              {(data?.segments ?? []).map((s) => {
                const fatia = (FATIAS as readonly string[]).includes(s.key) ? s.key : "outros";
                return (
                  <li key={s.key} className="flex items-center gap-2">
                    <span className="inline-block w-3 h-3 rounded-sm shrink-0" style={{ background: COR[fatia] }} />
                    <span className="flex-1 truncate">{s.label}</span>
                    <span className="tabular-nums font-medium">{s.n}</span>
                    <span className="tabular-nums text-muted-foreground w-10 text-right">{pct(s.n, total)}</span>
                  </li>
                );
              })}
              <li className="flex items-center gap-2 pt-1.5 mt-1 border-t border-border font-medium">
                <span className="w-3" />
                <span className="flex-1">Total</span>
                <span className="tabular-nums">{total}</span>
                <span className="w-10" />
              </li>
            </ul>
          </div>
        )}
      </CardContent>
    </Card>
  );
}
